import Input from '../../../components/ui/Input';
import { MONO_CLASS } from '../../../components/ui/Mono';
import { CASE_GTIN_LENGTHS, GTIN_LENGTHS, TAX_GTIN_LENGTHS } from '../../../constants/productCatalog';
import { ValidateGtin } from '../../../utils/productCodes';
import { BlingSuggestion, FieldCell, FieldInputProps, FormSection } from '../editorParts';
import { ResolveTaxEanFill } from '../productForm';

function validHint(value, lengths, fallback) {
    const result = ValidateGtin(value, { lengths });
    if (result.empty) {
        return fallback;
    }
    return result.ok ? `GTIN-${result.length} válido` : null;
}

/**
 * Três códigos, três papéis: `ean` é o GTIN do próprio SKU (o que se bipa no
 * produto na mão, cEAN); `tax_ean` é o GTIN-8/12/13 da unidade de varejo que
 * vai na NF (cEANTrib); `case_gtin` + unidades por caixa descrevem a caixa em
 * que o fornecedor entrega N deste SKU.
 */
const BarcodesSection = ({ ctx }) => {
    const { form, setField, readOnly, suggestions, capturedAt, mode, dirtyFields } = ctx;
    // A dica "grava igual ao GTIN do SKU" só quando o fill vai MESMO no payload
    // (BuildCreatePayload/BuildUpdatePayload): no create sempre que o campo está
    // vazio; na edição só quando a mudança veio do `ean` — esvaziar o tributário
    // de propósito grava NULL.
    const taxEanFill = ResolveTaxEanFill(form.ean, form.tax_ean);
    const taxEanFillActive =
        Boolean(taxEanFill) && (mode === 'new' || (dirtyFields?.has('ean') && !dirtyFields?.has('tax_ean')));
    const gtinInputProps = {
        inputMode: 'numeric',
        maxLength: 14,
        autoComplete: 'off',
        spellCheck: false,
        className: MONO_CLASS,
    };

    return (
        <FormSection
            id="codigos"
            title="Códigos de barras"
            description="GTIN aceita 8, 12, 13 ou 14 dígitos com dígito verificador GS1 e não se repete entre produtos."
        >
            <FieldCell span={3}>
                <Input
                    label="GTIN/EAN do SKU"
                    placeholder="Sem GTIN"
                    {...gtinInputProps}
                    {...FieldInputProps(ctx, 'ean', { hint: validHint(form.ean, GTIN_LENGTHS, 'Sem GTIN') })}
                />
                <BlingSuggestion
                    suggestion={suggestions.ean}
                    capturedAt={capturedAt}
                    disabled={readOnly}
                    onUse={(value) => setField('ean', value)}
                />
            </FieldCell>
            <FieldCell span={3}>
                <Input
                    label="GTIN tributário"
                    placeholder={taxEanFillActive ? taxEanFill : 'Sem GTIN'}
                    {...gtinInputProps}
                    {...FieldInputProps(ctx, 'tax_ean', {
                        hint: taxEanFillActive
                            ? 'Vazio: grava igual ao GTIN do SKU'
                            : validHint(
                                  form.tax_ean,
                                  TAX_GTIN_LENGTHS,
                                  'EAN da unidade de varejo (cEANTrib), 8/12/13 dígitos',
                              ),
                    })}
                />
                <BlingSuggestion
                    suggestion={suggestions.tax_ean}
                    capturedAt={capturedAt}
                    disabled={readOnly}
                    onUse={(value) => setField('tax_ean', value)}
                />
            </FieldCell>
            <FieldCell span={3}>
                <Input
                    label="GTIN da caixa do fornecedor"
                    placeholder="DUN-14"
                    {...gtinInputProps}
                    {...FieldInputProps(ctx, 'case_gtin', {
                        hint: validHint(
                            form.case_gtin,
                            CASE_GTIN_LENGTHS,
                            'Caixa em que o fornecedor entrega este SKU',
                        ),
                    })}
                />
            </FieldCell>
            <FieldCell span={3}>
                <Input
                    label="Unidades por caixa"
                    inputMode="numeric"
                    suffix="un"
                    autoComplete="off"
                    className={MONO_CLASS}
                    {...FieldInputProps(ctx, 'units_per_case', { hint: 'Quantas unidades deste SKU vêm na caixa' })}
                />
            </FieldCell>
        </FormSection>
    );
};

export default BarcodesSection;
